import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/constants/query-keys'
import { statesService } from '../services/states.service'

/**
 * Lookups de estados para consumidores que siguen en v1: builder de workflows,
 * acciones y filtros de negociaciones. Todos bindean `_id` y lo mandan a
 * endpoints v1, así que este lookup NO se puede migrar entero — muere cuando
 * migra cada módulo consumidor.
 *
 * La matriz de permisos del editor de roles v2 ya no pasa por acá: usa
 * `use-states-v2-lookup-query.ts`, que identifica por `sta_`.
 *
 * El listado admin vive en use-states-admin-query.ts.
 */
export interface IStateLookup {
  _id: string
  id: string
  name: string
  type: string
  color: string
}

// `useMyTransitionsQuery` se retiró el 2026-08-05 sin llamadores: los estados que el usuario
// puede leer los da `useMyStateTransitionsV2Query`, contra `GET /api/v2/me/state-transitions`.
// Con él se fue el handler v1 `GET /states/my-transitions`, primer ítem de la cola del carril C.

/**
 * El catálogo de estados. **Lo que se rompe si esto falla no es un desplegable vacío.**
 *
 * Las cuatro acciones de transición del expediente —cambiar de estado, rechazar, rescindir,
 * inactivar— resuelven acá el estado DESTINO de su botón (`states.find(type === 'reject')`, etc.)
 * y de ahí sacan su texto y su color. Con el catálogo vacío el destino queda `null` y el botón
 * **se dibuja igual, sin texto y sin color, y sigue disparando la mutación**: un rectángulo en
 * blanco que rechaza la negociación. Eso era peor que no mostrarlo.
 *
 * Hasta el 2026-08-05 el `queryFn` hacía `catch → console.error → return []`, así que ese vacío
 * llegaba como un ÉXITO y ninguna pantalla podía distinguir «falló» de «no hay estados». Sin el
 * `catch`, el fallo viaja como `isError` y el layout del expediente lo dibuja UNA vez, arriba de
 * las acciones, en vez de cuatro botones mudos.
 *
 * ⚠️ Los otros tres consumidores —el builder de workflows, el editor de roles y las acciones del
 * panel de detalle— siguen con `= []` por defecto, así que **para ellos no cambia nada**: se
 * quedan con el vacío silencioso. No era olvido, era alcance: son de otros dominios y darles rama
 * de error es rediseñarles la pantalla.
 *
 * ✅ **La decisión Nº 19 se resolvió el 2026-08-11 y este catálogo deja de estar a medias**: se
 * barren todos, así que los tres consumidores restantes —el builder de workflows, el editor de
 * roles y las acciones del panel de detalle— entran a la tanda como el resto.
 */
export const useStatesLookupQuery = (dependence = true) => {
  return useQuery<IStateLookup[]>({
    queryKey: queryKeys.states.lookup(),
    queryFn: () => statesService.getLookup(),
    refetchOnWindowFocus: false,
    enabled: dependence
  })
}
